import { Injectable } from '@angular/core';
import { INDRAAddOnAncillary, INDRAAddOnShoppingCart } from "@ibe.models/ancillary.indra.model.lib";
import { BehaviorSubject, Observable } from "rxjs";
import { INDRAStorageService } from './storage.indra.service';
import { INDRABFMDataStorageService } from '@ibe.services/bfm-data-storage.indra.service';
import { ANCILLARY_GROUP_SEAT_RESERVATION_LIST, UNCONFIRMED_CODES, URL_SHOPPING_CART } from '@ibe.config';
import { INDRABookingService } from '@ibe.services/booking.indra.service';
import { INDRAApiService } from './api.indra.service';

@Injectable({
	providedIn: 'root'
})
export class INDRAAncillariesShoppingCartService {

	private pnr: string;
	private addedAncillaries: INDRAAddOnAncillary[];
	private numAddedAncillaries: number;
	private currencyCode: string;
	private shoppingCart$: BehaviorSubject<INDRAAddOnShoppingCart>;

	constructor(private storageService: INDRAStorageService,
		private bfmStorage: INDRABFMDataStorageService,
		private bookingService: INDRABookingService,
		private apiService:INDRAApiService
		) {
		const shoppingCart = storageService.get("INDRAAncillariesShoppingCartService.shoppingCart");
		if (shoppingCart) {
			this.pnr = shoppingCart.pnr;
			this.addedAncillaries = shoppingCart.itemsAdded;
			this.numAddedAncillaries = shoppingCart.numItems;
			this.currencyCode = shoppingCart.currencyCode;
			this.shoppingCart$ = new BehaviorSubject(shoppingCart);

			this.shoppingCart$.subscribe(lastValue => {
				this.storageService.set("INDRAAncillariesShoppingCartService.shoppingCart", lastValue);
			})
		}
	}

	public INDRA_getCurrencyCode(): string {
		return (this.currencyCode ? this.currencyCode : '');
	}

	public INDRA_getAddedAncillaries(): INDRAAddOnAncillary[] {
		return this.addedAncillaries;
	}

	public INDRA_getAncillariesShoppingCart(currentPNR: string): Observable<INDRAAddOnShoppingCart> {
		let shoppingCart = this.storageService.get("INDRAAncillariesShoppingCartService.shoppingCart");
		if (!shoppingCart || shoppingCart.pnr !== currentPNR) {
			this.pnr = currentPNR;
			this.addedAncillaries = [];
			this.numAddedAncillaries = 0;
			this.currencyCode = '';
			shoppingCart = { pnr: currentPNR, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currency: this.currencyCode };

			this.shoppingCart$ = new BehaviorSubject(shoppingCart);

			this.shoppingCart$.subscribe(lastValue => {
				this.storageService.set("INDRAAncillariesShoppingCartService.shoppingCart", lastValue);
			})
		}

		return this.shoppingCart$.asObservable();
	}

	public INDRA_addAncillaries(addedAncillaries: INDRAAddOnAncillary[]) {
		addedAncillaries.forEach(
			addOnAncillary => {
				this.addedAncillaries.push(addOnAncillary);
				this.numAddedAncillaries++;
				this.currencyCode = addOnAncillary.currency;
			}
		);

		this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
	}

	public INDRA_updateAncillaries(ancillary) {
		this.addedAncillaries.forEach(addedAncillary => {
			if (addedAncillary.type === ancillary[0].type) {
				addedAncillary.payWithMiles = ancillary[0].payWithMiles;
			}
		})
		this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
	}

	// this will update addedAncillaries and shoppingCart prices due to tier benefits 
	public INDRA_updateAddedAncillariesPrices(ancillaryShoppingCart) {
		this.addedAncillaries = ancillaryShoppingCart.itemsAdded;
		this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
	}

	public INDRA_deleteAncillary(deletedAncillary: INDRAAddOnAncillary) {
		const deletedAncillaries: INDRAAddOnAncillary[] = [deletedAncillary];
		this.INDRA_deleteAncillaries(deletedAncillaries);
	}

	public INDRA_deleteAncillaries(deletedAncillaries: INDRAAddOnAncillary[]) {
		deletedAncillaries.forEach(
			deletedAncillary => {
				const index = this.addedAncillaries.findIndex(addOn =>
					addOn.paxId === deletedAncillary.paxId &&
					addOn.code === deletedAncillary.code &&
					addOn.flightId === deletedAncillary.flightId &&
					addOn.segmentId === deletedAncillary.segmentId &&
					addOn.ssrTatto === deletedAncillary.ssrTatto
				);

				if (index >= 0) {
					this.numAddedAncillaries--;
					this.addedAncillaries.splice(index, 1);
				}
			}
		);

		this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
	}

	public INDRA_emptyAncillariesShoppingCart(optionCallDeleteAPI: boolean =  true) {
		this.addedAncillaries = [];
		this.numAddedAncillaries = 0;
		this.currencyCode = '';
		if (this.shoppingCart$) {
			this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
			if (optionCallDeleteAPI) {
				return this.apiService.INDRA_delete(URL_SHOPPING_CART, null);
			} else {
				const resp = { 'status': '200', 'errors': [], 'timeService': null, 'timeBackend': null, 'selectedAncillaries': [] };
				return new BehaviorSubject(resp).asObservable();
			}
		}
	}

	public INDRA_getAddedAncillariesByFlightCardId(itemType: string, flightCardId: string): INDRAAddOnAncillary[] {
		return this.addedAncillaries.filter(item => ((item.type === itemType || item.code === itemType) && item.flightCardId.toString() === flightCardId.toString()));
	}

	public INDRA_getAddedAncillariesSegmentBased(itemType: string, flightId: number, segmentId: number): INDRAAddOnAncillary[] {
		return this.addedAncillaries.filter(item => (item.flightBased === false && item.type === itemType && item.flightId === flightId && item.segmentId === segmentId));
	}
	public INDRA_getAddedAncillariesFlightBased(itemType: string, flightId: number, segmentsIds: number[]): INDRAAddOnAncillary[] {
		return this.addedAncillaries.filter(item => (item.flightBased === true && item.type === itemType && item.flightId === flightId && segmentsIds.includes(item.segmentId)));
	}


	// SEATS SPECIFIC METHODS

	public INDRA_addSeats(addedSeats: INDRAAddOnAncillary[], deletedSeats: INDRAAddOnAncillary[]) {

		const existingSeats = [];

		/**
		 *  Removes deleted seat in addedSeats
		 */
		deletedSeats.forEach(
			deletedAncillary => {
				const index = this.addedAncillaries.findIndex(addOn =>
					addOn.paxId === deletedAncillary.paxId &&
					addOn.code === deletedAncillary.code &&
					addOn.flightCardId === deletedAncillary.flightCardId
				);
				if (index >= 0) {
					this.numAddedAncillaries--;
					this.addedAncillaries.splice(index, 1);
					addedSeats.splice(index, 1);
				}
			}
		);

		/**
		 *  Removes seat to prevent multiple seats
		 *  being selected by a single passenger
		 */
		if (addedSeats.length > 0) {
			addedSeats.forEach(element => {
				const existingSeat = this.addedAncillaries.filter(
					ancillary => {
						return ANCILLARY_GROUP_SEAT_RESERVATION_LIST.includes(ancillary.type)
							&& ancillary.paxId === element.paxId
							&& ancillary.flightId === element.flightId
							&& ancillary.segmentId === element.segmentId;
					}
				)
				if (existingSeat.length > 0) {
					existingSeats.push(existingSeat)
				}

			});

			if (existingSeats.length > 0) {
				existingSeats.forEach(seat => {
					this.addedAncillaries.splice(addedSeats.indexOf(seat));
					this.numAddedAncillaries--;
				});
			}

			/**
			 *  Finalizing added ancillaries
			 */
			addedSeats.forEach(
				addOnAncillary => {
					this.addedAncillaries.push(addOnAncillary);
					this.numAddedAncillaries++;
					this.currencyCode = addOnAncillary.currency;
				}
			);
		}

		// will be used to persist new seats into the pnr data
		// this.INDRA_UpdateValues(addedSeats);
		this.shoppingCart$.next({ pnr: this.pnr, numItems: this.numAddedAncillaries, itemsAdded: this.addedAncillaries, currencyCode: this.currencyCode });
	}


	public INDRA_UpdateValues(ancillarySeat) {
		/**
		 * 	This method will be used in persisting
		 *  new seats in the pnr data
		 */
		const journey = [];
		ancillarySeat.forEach(seat => {
			journey.push(
				{
					"flightId": seat.flightId,
					"segmentId": seat.segmentId,
					"passengers": [
						{
							"paxId": seat.paxId,
							"paxTatoo": seat.paxTatto,
							"ancillaryInfo": [
								{
									"price": seat.price,
									"code": seat.code,
									"seatCode": seat.value
								}
							]
						}
					]
				}
			)
		});

		const dataHolder = this.bfmStorage.INDRA_getActiveBookedTrip();
		dataHolder.data.ancillaries.seat.journey = journey;
		// this.bfmStorage.INDRA_setActiveBookedTrip(dataHolder);
		// this.bfmStorage.observableCurrentBooking.next(this.bfmStorage.INDRA_getActiveBookedTrip());
	}

	INDRA_getStatusKey(statusCode: number) {
		switch(statusCode) {
			case 41: return 'bookingDetails.text.notPossibleToConfirm';
			case 22: return 'bookingDetails.text.unableToConfirm';
			case 46: return 'bookingDetails.text.pendingConfirmation';
		}
	}

	INDRA_getAllWarnings(flightId: string, paramCode: string) {
		let warnings = [];
		const ancillaryGroup = this.bookingService.INDRA_getSSRAvailableForGroup(paramCode);
		
		if (this.bfmStorage.activeBooking.data.ancillaries.ancillaries && this.bfmStorage.activeBooking.data.ancillaries.ancillaries.length) {
			this.bfmStorage.activeBooking.data.ancillaries.ancillaries.forEach((ancillary) => {
				ancillary.journey.forEach((journey) => {
					if (journey.flightId === flightId) {
						journey.passengers.forEach((passenger) => {
							passenger.ancillaryInfo.forEach((info) => {
								let status = parseInt(info.status);
								if (UNCONFIRMED_CODES.includes(status) && ancillaryGroup.includes(info.code)) {
									if (!warnings.includes(status)) {
										warnings.push(status);
									}
								}
							});
						});
					}
				})
			});
		}
	
		this.bfmStorage.activeBooking.data.infoPax.listPax.forEach((pax) => {
			if (pax.prm) {
				pax.prm.forEach((paxPrm) => {
					const statusCode = parseInt(paxPrm.status);
					if (UNCONFIRMED_CODES.includes(statusCode) && ancillaryGroup.includes(paxPrm.type)) {
						if (!warnings.includes(statusCode)) {
							warnings.push(statusCode);
						}
					}
				});
			}
		});
		return warnings;
	}

	INDRA_getSeatWarnings(flightCardId: string) {
		let warnings = [];
		const segmentIDs = this.bookingService.INDRA_getSegmentIDs(flightCardId);

		this.bfmStorage.activeBooking.data.ancillaries.seat.journey.forEach(journey => {
			if (segmentIDs.includes(journey.segmentId)) {
				journey.passengers.forEach(pax => {
					pax.ancillaryInfo.forEach(ancillaryInfoItem => {
						let status = parseInt(ancillaryInfoItem.status);
						if (UNCONFIRMED_CODES.includes(status)) {
							if (!warnings.includes(status)) {
								warnings.push(status);
							}
						}
					});
				});
			}
		});
		return warnings;
	}
}
